import React, { FC, useEffect, useRef, useState } from "react";

import { useAnalyzeWebsiteMutation, useGetWebsiteAnalysisQuery } from "generated/graphql";
import { useBoolState } from "utils/bool";
import { wrapUrqlMutation } from "utils/graphql";

import { OnboardingProps } from "components/Onboarding/models";
import { TrainingStep, TrainingStepProps } from "../../components/V3";

export interface TrainingStepContainerProps extends TrainingStepProps {
    /**
     * Timeout in milliseconds to wait before just moving on
     */
    timeout?: number;
    /**
     * On initial data
     */
    onInitialData(onboarding: OnboardingProps): void;
    /**
     * We reached the timeout
     */
    onTimeout(onboarding: OnboardingProps): void;
}

export const TrainingStepContainer: FC<TrainingStepContainerProps> = (props) => {
    const { timeout, onInitialData, onTimeout, onboarding, ...rest } = props;

    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const [mutableOnboarding, setMutableOnboarding] = useState<OnboardingProps>(onboarding);

    const [analyzing, , startAnalyzing] = useBoolState(false);

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: true,
    });

    const [getAnalysis, executeGetAnalysis] = useGetWebsiteAnalysisQuery({
        pause: !mutableOnboarding.executionId,
        requestPolicy: "cache-and-network",
        variables: {
            executionId: mutableOnboarding.executionId,
        },
    });

    const website = mutableOnboarding.place?.website || mutableOnboarding.website;

    useEffect(() => {
        const newWebsite = onboarding.place?.website || onboarding.website;

        if (website !== newWebsite) {
            console.log("updating props because of website");
            console.log(onboarding);
            setMutableOnboarding(onboarding);
        }
    }, [onboarding, mutableOnboarding.website, website]);

    const { data: analysisData } = getAnalysis;

    useEffect(() => {
        if (!website) {
            return;
        }

        // start the training
        if (!analyzing) {
            startAnalyzing();

            const url = new URL(website || "");
            // remove query params
            url.search = "";
            const cleanWebsite = url.toString();

            analyzeSite({ url: cleanWebsite }).then((result) => {
                const executionId = result.data?.onboarding?.startRetrieveWebsiteDetails?.executionId;
                setMutableOnboarding((prev) => ({ ...prev, executionId }));
            });
        }
    }, [analyzeSite, analyzing, startAnalyzing, website]);

    useEffect(() => {
        if (!timerRef.current) {
            timerRef.current = setTimeout(() => {
                onTimeout(mutableOnboarding);
            }, timeout);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [mutableOnboarding, onTimeout, timeout]);

    useEffect(() => {
        // this gets called a LOT
        if (analysisData?.onboarding?.webDetailsExecution.data && mutableOnboarding.executionId) {
            // update mutableOnboarding with what we have
            console.log("Received initial data");
            console.log(analysisData?.onboarding?.webDetailsExecution);
            // grab some important data
            const details = analysisData?.onboarding?.webDetailsExecution.data;
            const business = details.business;
            const site = details.site;

            console.log(business);
            const businessName = business?.name;

            // TODO: Add back logo to query getWebsiteAnalysis
            // const businessLogo = business?.logo;

            const businessLogo = site?.logo;

            // theme
            const primaryColor = site?.primaryColor;
            const secondaryColor = site?.secondaryColor;

            let update: boolean = false;

            const dataCopy = { ...mutableOnboarding };

            if (!dataCopy.name && businessName) {
                update = true;
                dataCopy.name = businessName;
            }

            if (!dataCopy.logo && businessLogo) {
                update = true;
                dataCopy.logo = businessLogo;
            }

            if (!dataCopy.primaryColor && primaryColor) {
                update = true;
                dataCopy.primaryColor = primaryColor;
            }

            if (!dataCopy.secondaryColor && secondaryColor) {
                update = true;
                dataCopy.secondaryColor = secondaryColor;
            }

            if (update) {
                console.log("updating onboarding from website analysis", dataCopy);
                setMutableOnboarding(dataCopy);
            }
        }
    }, [analysisData?.onboarding?.webDetailsExecution, mutableOnboarding]);

    useEffect(() => {
        if (analysisData?.onboarding?.webDetailsExecution?.completed) {
            console.log("TrainingStep: Analysis complete", website);

            // Clear the timeout before moving on
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            onInitialData(mutableOnboarding);
        }
    }, [analysisData, onInitialData, mutableOnboarding, website]);

    useEffect(() => {
        // this polls the executeGetAnalysis query until it is done.
        if (mutableOnboarding.executionId && !getAnalysis.fetching) {
            const id = setTimeout(() => {
                executeGetAnalysis({ requestPolicy: "network-only" });
            }, 2000);
            return () => {
                clearTimeout(id);
            };
        }
    }, [getAnalysis.fetching, executeGetAnalysis, mutableOnboarding.executionId]);

    return <TrainingStep {...rest} onboarding={onboarding} />;
};
